import { useMemo, useState } from "react";
import {
    ActivityIcon,
    ArrowDownIcon,
    ArrowUpIcon,
    ArrowUpDownIcon,
    ChevronLeftIcon,
    ExternalLinkIcon,
    MicIcon,
    SearchIcon,
    XIcon,
} from "lucide-react";
import { ActivityLog } from "@component/training/activity-log";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Card, CardContent } from "@component/card";
import { RubricBar, rubricGradeBand } from "@component/training/rubric-bar";
import { ScoreSparkline } from "@component/training/score-sparkline";
import { TrainingStatusPill } from "@component/training/status-pill";
import type { ActivityEvent } from "@service/training";
import type { RubricScores, SessionStatus, TrainingSession } from "@type/training";
import { RUBRIC_LABELS } from "@type/training";
import { cn } from "@util/tailwind";

type SortKey = "attempt" | "date" | "status" | "score";
type SortDir = "asc" | "desc";
// "performance" is EP A mode; "campaigns" is EP B mode.
type Tab = "performance" | "campaigns" | "activity";
type CampaignStatusFilter = "all" | "completed" | "in_progress" | "invited";

interface Props {
    open: boolean;
    onClose: () => void;
    name: string;
    email: string;
    role?: string | null;
    department?: string | null;
    personaName: string;
    attempts: TrainingSession[];
    timeline?: ActivityEvent[];
    // True while the attempts/timeline are being fetched — renders a skeleton.
    loading?: boolean;
    // EP A mode: pass the campaign name to show a "Showing attempts for X"
    // indicator and keep the single-campaign "Training Performance" tab.
    // EP B mode: omit this prop — the drawer switches to a multi-campaign
    // "Campaigns" tab that groups all attempts by campaign_id.
    campaignName?: string;
}

interface CampaignGroup {
    campaignId: string;
    campaignName: string;
    personaName: string;
    teamLeader?: string | null;
    attempts: TrainingSession[];
}

function initials(name: string) {
    return name
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

function formatDateGB(dateStr: string | null | undefined): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

function deriveCampaignStatus(attempts: TrainingSession[]): CampaignStatusFilter {
    if (attempts.some((a) => a.status === "completed")) return "completed";
    if (attempts.some((a) => a.status === "in_progress" || a.status === "started")) return "in_progress";
    return "invited";
}

export function EmployeeProfileDrawer({
    open,
    onClose,
    name,
    email,
    role,
    department,
    personaName,
    attempts,
    timeline = [],
    loading = false,
    campaignName,
}: Props) {
    const isEpA = !!campaignName;

    const [activeTab, setActiveTab] = useState<Tab>(isEpA ? "performance" : "campaigns");
    const [sortKey, setSortKey] = useState<SortKey>("attempt");
    const [sortDir, setSortDir] = useState<SortDir>("asc");

    // EP B campaign list filters + sub-page navigation
    const [campaignSearch, setCampaignSearch] = useState("");
    const [campaignStatusFilter, setCampaignStatusFilter] = useState<CampaignStatusFilter>("all");
    const [drawerView, setDrawerView] = useState<"list" | "detail">("list");
    const [detailGroup, setDetailGroup] = useState<CampaignGroup | null>(null);

    const sortedAttempts = [...attempts].sort((a, b) => a.attempt_number - b.attempt_number);

    const active = sortedAttempts[sortedAttempts.length - 1];
    const scoredActive =
        [...sortedAttempts].reverse().find(
            (a) => a.total_score !== null && a.rubric_scores != null,
        ) ?? null;

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir(key === "attempt" ? "asc" : "desc");
        }
    };

    const historySorted = useMemo(() => {
        const STATUS_RANK: Record<string, number> = {
            completed: 0,
            in_progress: 1,
            started: 2,
            invited: 3,
            abandoned: 4,
            expired: 5,
        };
        const arr = [...sortedAttempts];
        arr.sort((a, b) => {
            let cmp = 0;
            if (sortKey === "attempt") cmp = a.attempt_number - b.attempt_number;
            else if (sortKey === "date") {
                const at = a.completed_at ? new Date(a.completed_at).getTime() : 0;
                const bt = b.completed_at ? new Date(b.completed_at).getTime() : 0;
                cmp = at - bt;
            } else if (sortKey === "status") {
                cmp = (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
            } else if (sortKey === "score") {
                cmp = (a.total_score ?? -1) - (b.total_score ?? -1);
            }
            return sortDir === "asc" ? cmp : -cmp;
        });
        return arr;
    }, [sortedAttempts, sortKey, sortDir]);

    // EP B: group all attempts by campaign_id, sorted by most recent activity.
    const campaignGroups = useMemo<CampaignGroup[]>(() => {
        if (isEpA) return [];
        const map = new Map<string, CampaignGroup>();
        for (const a of attempts) {
            const cid = a.campaign_id;
            if (!map.has(cid)) {
                map.set(cid, {
                    campaignId: cid,
                    campaignName: a.campaign_name ?? cid,
                    personaName: a.persona_name ?? "Persona",
                    teamLeader: a.team_leader ?? null,
                    attempts: [],
                });
            }
            map.get(cid)!.attempts.push(a);
        }
        const groups = Array.from(map.values()).map((g) => ({
            ...g,
            attempts: g.attempts.sort((a, b) => a.attempt_number - b.attempt_number),
        }));
        // Sort by most recent attempt activity (completed_at or started_at desc).
        groups.sort((a, b) => {
            const aLatest = a.attempts[a.attempts.length - 1];
            const bLatest = b.attempts[b.attempts.length - 1];
            const aTime = new Date(aLatest?.completed_at ?? aLatest?.started_at ?? 0).getTime();
            const bTime = new Date(bLatest?.completed_at ?? bLatest?.started_at ?? 0).getTime();
            return bTime - aTime;
        });
        return groups;
    }, [attempts, isEpA]);

    const searchFilteredGroups = useMemo(() => {
        if (!campaignSearch) return campaignGroups;
        const q = campaignSearch.toLowerCase();
        return campaignGroups.filter((g) => g.campaignName.toLowerCase().includes(q));
    }, [campaignGroups, campaignSearch]);

    const statusCounts = useMemo(() => {
        const counts = { all: searchFilteredGroups.length, completed: 0, in_progress: 0, invited: 0 };
        for (const g of searchFilteredGroups) {
            const s = deriveCampaignStatus(g.attempts);
            if (s === "completed") counts.completed++;
            else if (s === "in_progress") counts.in_progress++;
            else counts.invited++;
        }
        return counts;
    }, [searchFilteredGroups]);

    const filteredGroups = useMemo(() => {
        if (campaignStatusFilter === "all") return searchFilteredGroups;
        return searchFilteredGroups.filter(
            (g) => deriveCampaignStatus(g.attempts) === campaignStatusFilter,
        );
    }, [searchFilteredGroups, campaignStatusFilter]);

    if (!open) return null;

    const startedAttempts = sortedAttempts.filter((a) => a.started_at != null);
    const attemptCount = active?.attempt_number ?? startedAttempts.length;
    const scores: RubricScores | null = scoredActive?.rubric_scores ?? null;
    const total = scoredActive?.total_score ?? null;
    const band = total !== null ? rubricGradeBand(total) : null;
    const latestStatus = sortedAttempts[sortedAttempts.length - 1]?.status;

    const tabs: { key: Tab; label: string; count?: number }[] = isEpA
        ? [
              { key: "performance", label: "Training Performance" },
              { key: "activity", label: "Activity Log" },
          ]
        : [
              { key: "campaigns", label: "Campaigns", count: campaignGroups.length },
          ];

    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex justify-end"
            onClick={onClose}
        >
            <div
                className="bg-white w-full max-w-2xl h-full flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">Employee Profile</h2>
                    <div className="flex items-center gap-3">
                        {isEpA ? (
                            campaignName && (
                                <p className="text-xs text-gray-400">
                                    Showing attempts for{" "}
                                    <span className="font-medium text-gray-600">{campaignName}</span>
                                </p>
                            )
                        ) : (
                            <p className="text-xs text-gray-400">Showing all assigned campaigns</p>
                        )}
                        <button
                            onClick={onClose}
                            className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                            aria-label="Close"
                        >
                            <XIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Employee info */}
                <div className="px-6 pt-5 pb-4 flex-shrink-0">
                    <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-lg flex-shrink-0">
                            {initials(name)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                                <h3 className="text-xl font-semibold text-gray-900 leading-tight">
                                    {name}
                                </h3>
                                {loading ? (
                                    <span className="h-5 w-20 bg-gray-100 rounded-full animate-pulse flex-shrink-0" />
                                ) : isEpA && latestStatus ? (
                                    <TrainingStatusPill status={latestStatus} />
                                ) : null}
                            </div>
                            {(role || department) && (
                                <p className="text-sm text-gray-600 mt-0.5">
                                    {[role, department].filter(Boolean).join(" · ")}
                                </p>
                            )}
                            <p className="text-sm text-gray-400 mt-0.5">{email}</p>
                        </div>
                    </div>

                </div>

                {/* Tab navigation */}
                <div className="border-b border-gray-200 flex-shrink-0">
                    <div className="flex px-6">
                        {tabs.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => {
                                        // Clicking "Campaigns" tab from detail view returns to list
                                        if (!isEpA && t.key === "campaigns" && drawerView === "detail") {
                                            setDrawerView("list");
                                            setDetailGroup(null);
                                            return;
                                        }
                                        setActiveTab(t.key);
                                    }}
                                    className={cn(
                                        "py-2.5 px-4 text-sm font-medium relative whitespace-nowrap inline-flex items-center gap-2 border-b-2 -mb-px transition-colors",
                                        activeTab === t.key || (t.key === "campaigns" && drawerView === "detail")
                                            ? "border-blue-600 text-blue-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700",
                                    )}
                                >
                                    {t.label}
                                    {t.count !== undefined && !loading && (
                                        <span className={cn(
                                            "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold tabular-nums",
                                            activeTab === t.key || (t.key === "campaigns" && drawerView === "detail")
                                                ? "bg-blue-100 text-blue-700"
                                                : "bg-gray-100 text-gray-500",
                                        )}>
                                            {t.count}
                                        </span>
                                    )}
                                </button>
                            ))}
                    </div>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto flex flex-col">
                    {loading ? (
                        <DrawerSkeleton />
                    ) : (
                    <>
                    {/* EP A — Training Performance tab */}
                    {activeTab === "performance" && startedAttempts.length === 0 && (
                        <EmptyPerformance />
                    )}
                    {activeTab === "performance" && startedAttempts.length > 0 && (
                        <div className="px-6 py-5 pb-12 space-y-5">
                            <PerformanceContent
                                attempts={sortedAttempts}
                                personaName={personaName}
                                sortKey={sortKey}
                                sortDir={sortDir}
                                historySorted={historySorted}
                                attemptCount={attemptCount}
                                scoredActive={scoredActive}
                                scores={scores}
                                total={total}
                                band={band}
                                onSort={handleSort}
                            />
                        </div>
                    )}

                    {/* EP B — Campaigns tab: card list or campaign detail sub-page */}
                    {activeTab === "campaigns" && drawerView === "list" && (
                        <div className="flex flex-col flex-1 min-h-0">
                            {/* Search + filter bar */}
                            <div className="px-6 py-3 flex gap-2 flex-shrink-0">
                                <div className="relative flex-1 min-w-0">
                                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search campaigns..."
                                        value={campaignSearch}
                                        onChange={(e) => setCampaignSearch(e.target.value)}
                                        className="w-full h-9 pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                    />
                                </div>
                                <Select
                                    value={campaignStatusFilter}
                                    onValueChange={(v) => setCampaignStatusFilter(v as CampaignStatusFilter)}
                                >
                                    <SelectTrigger className="w-[200px] text-sm flex-shrink-0">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            All Statuses <span className="text-gray-400">({statusCounts.all})</span>
                                        </SelectItem>
                                        {statusCounts.completed > 0 && (
                                            <SelectItem value="completed">
                                                Completed <span className="text-gray-400">({statusCounts.completed})</span>
                                            </SelectItem>
                                        )}
                                        {statusCounts.in_progress > 0 && (
                                            <SelectItem value="in_progress">
                                                In Progress <span className="text-gray-400">({statusCounts.in_progress})</span>
                                            </SelectItem>
                                        )}
                                        {statusCounts.invited > 0 && (
                                            <SelectItem value="invited">
                                                Invited <span className="text-gray-400">({statusCounts.invited})</span>
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                {campaignGroups.length === 0 ? (
                                    <EmptyPerformance />
                                ) : filteredGroups.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                        <p className="text-sm font-semibold text-gray-700 mb-1">No campaigns match.</p>
                                        <p className="text-sm text-gray-500">Try adjusting your search or status filter.</p>
                                    </div>
                                ) : (
                                    <div className="px-6 pb-4 space-y-3">
                                        {filteredGroups.map((group) => (
                                            <CampaignCard
                                                key={group.campaignId}
                                                group={group}
                                                onClick={() => {
                                                    setDetailGroup(group);
                                                    setDrawerView("detail");
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === "campaigns" && drawerView === "detail" && detailGroup && (
                        <CampaignDetailPage
                            group={detailGroup}
                            onBack={() => { setDrawerView("list"); setDetailGroup(null); }}
                        />
                    )}

                    {activeTab === "activity" && (
                        timeline.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center px-4 text-center">
                                <div className="mb-3 rounded-full bg-gray-100 p-3">
                                    <ActivityIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <p className="text-sm font-semibold text-gray-700 mb-1">
                                    No activity yet.
                                </p>
                                <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
                                    Their training activity will appear here as they take roleplays.
                                </p>
                            </div>
                        ) : (
                            <div className="px-6 py-5 pb-12">
                                <ActivityLog events={timeline} collapsible={false} />
                            </div>
                        )
                    )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Shared sub-components ───────────────────────────────────────────────────

function EmptyPerformance() {
    return (
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center">
            <div className="mb-3 rounded-full bg-gray-100 p-3">
                <MicIcon className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">
                No roleplay sessions yet.
            </p>
            <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
                Scores and feedback will appear once they complete their first roleplay.
            </p>
        </div>
    );
}

function PerformanceContent({
    attempts,
    personaName,
    sortKey,
    sortDir,
    historySorted,
    attemptCount,
    scoredActive,
    scores,
    total,
    band,
    onSort,
}: {
    attempts: TrainingSession[];
    personaName: string;
    sortKey: SortKey;
    sortDir: SortDir;
    historySorted: TrainingSession[];
    attemptCount: number;
    scoredActive: TrainingSession | null;
    scores: RubricScores | null;
    total: number | null;
    band: { label: string; cls: string } | null;
    onSort: (key: SortKey) => void;
}) {
    const startedAttempts = attempts.filter((a) => a.started_at != null);
    return (
        <>
            {/* Score over time */}
            <Card>
                <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold text-gray-800">
                            Score over time
                        </div>
                        <div className="text-xs text-gray-500">
                            {attemptCount} attempt{attemptCount === 1 ? "" : "s"}
                        </div>
                    </div>
                    <ScoreSparkline
                        attempts={startedAttempts.map((a) => ({
                            attempt_number: a.attempt_number,
                            total_score: a.total_score,
                        }))}
                        width={430}
                        height={84}
                        showValues
                    />
                </CardContent>
            </Card>

            {/* Latest attempt summary */}
            {scoredActive && total !== null && band && scores && (
                <Card>
                    <CardContent className="p-5">
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="text-sm font-semibold text-gray-800">
                                Attempt {scoredActive.attempt_number} · {personaName}
                            </div>
                            <span
                                className={cn(
                                    "inline-block px-2 py-0.5 border rounded-full text-[10px] font-semibold uppercase tracking-wide",
                                    band.cls,
                                )}
                            >
                                {band.label}
                            </span>
                        </div>
                        <div className="flex items-baseline gap-2 mb-3">
                            <span className="text-5xl font-bold text-gray-900 tabular-nums leading-none">
                                {total}
                            </span>
                            <span className="text-xl text-gray-400">/100</span>
                        </div>

                        {scoredActive.qualitative_feedback && (
                            <div className="mb-4">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                                    Roleplay Performance Insights
                                </p>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {scoredActive.qualitative_feedback}
                                </p>
                            </div>
                        )}

                        <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                                    Rubric Score
                                </p>
                                <span className="text-sm font-bold text-gray-900 tabular-nums">
                                    {total}<span className="text-gray-400 font-normal">/100</span>
                                </span>
                            </div>
                            {(Object.keys(RUBRIC_LABELS) as (keyof RubricScores)[]).map((k) => (
                                <RubricBar key={k} label={RUBRIC_LABELS[k]} value={scores[k]} />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Attempt history */}
            {attempts.length > 0 && (
                <Card>
                    <CardContent className="p-5">
                        <div className="text-sm font-semibold text-gray-800 mb-3">
                            Attempt history
                        </div>
                        <AttemptHistoryTable
                            attempts={attempts}
                            historySorted={historySorted}
                            sortKey={sortKey}
                            sortDir={sortDir}
                            onSort={onSort}
                        />
                    </CardContent>
                </Card>
            )}
        </>
    );
}


function CampaignCard({
    group,
    onClick,
}: {
    group: CampaignGroup;
    onClick: () => void;
}) {
    const sortedAttempts = [...group.attempts].sort((a, b) => a.attempt_number - b.attempt_number);
    const startedAttempts = sortedAttempts.filter((a) => a.started_at != null);
    const attemptCount = startedAttempts.length;
    const derivedStatus = deriveCampaignStatus(sortedAttempts);
    const latestActivity = sortedAttempts[sortedAttempts.length - 1]?.completed_at
        ?? sortedAttempts[sortedAttempts.length - 1]?.started_at;

    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full text-left group"
        >
            <Card className="border border-gray-200 group-hover:border-blue-400 transition-colors cursor-pointer">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate mb-0.5">
                                {group.campaignName}
                            </p>
                            <p className="text-xs text-gray-500 mb-2.5 truncate">{group.personaName}</p>
                            <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-500">
                                <span>Total attempts: <span className="font-medium text-gray-700">{attemptCount}</span></span>
                                {latestActivity && (
                                    <>
                                        <span className="text-gray-300">·</span>
                                        <span>Last attempted: <span className="font-medium text-gray-700">{formatDateGB(latestActivity)}</span></span>
                                    </>
                                )}
                                {group.teamLeader && (
                                    <>
                                        <span className="text-gray-300">·</span>
                                        <span>Team Leader: <span className="font-medium text-gray-700">{group.teamLeader}</span></span>
                                    </>
                                )}
                            </div>
                        </div>
                        <TrainingStatusPill status={derivedStatus as SessionStatus} className="flex-shrink-0" />
                    </div>
                </CardContent>
            </Card>
        </button>
    );
}

function CampaignDetailPage({ group, onBack }: { group: CampaignGroup; onBack: () => void }) {
    const [sortKey, setSortKey] = useState<SortKey>("attempt");
    const [sortDir, setSortDir] = useState<SortDir>("asc");

    const sortedAttempts = useMemo(
        () => [...group.attempts].sort((a, b) => a.attempt_number - b.attempt_number),
        [group.attempts],
    );
    const startedAttempts = sortedAttempts.filter((a) => a.started_at != null);
    const scoredActive =
        [...sortedAttempts].reverse().find(
            (a) => a.total_score !== null && a.rubric_scores != null,
        ) ?? null;
    const scores: RubricScores | null = scoredActive?.rubric_scores ?? null;
    const total = scoredActive?.total_score ?? null;
    const band = total !== null ? rubricGradeBand(total) : null;
    const attemptCount = startedAttempts.length;
    const derivedStatus = deriveCampaignStatus(sortedAttempts);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir(key === "attempt" ? "asc" : "desc"); }
    };

    const STATUS_RANK: Record<string, number> = {
        completed: 0, in_progress: 1, started: 2, invited: 3, abandoned: 4, expired: 5,
    };
    const historySorted = useMemo(() => {
        const arr = [...sortedAttempts];
        arr.sort((a, b) => {
            let cmp = 0;
            if (sortKey === "attempt") cmp = a.attempt_number - b.attempt_number;
            else if (sortKey === "date") {
                const at = a.completed_at ? new Date(a.completed_at).getTime() : 0;
                const bt = b.completed_at ? new Date(b.completed_at).getTime() : 0;
                cmp = at - bt;
            } else if (sortKey === "status") {
                cmp = (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
            } else if (sortKey === "score") {
                cmp = (a.total_score ?? -1) - (b.total_score ?? -1);
            }
            return sortDir === "asc" ? cmp : -cmp;
        });
        return arr;
    }, [sortedAttempts, sortKey, sortDir]);

    return (
        <div className="flex-1 overflow-y-auto flex flex-col">
            {/* Campaign identity row: back button · name + status · persona · view link */}
            <div className="px-6 py-4 flex items-start justify-between gap-3 flex-shrink-0">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <button
                        type="button"
                        onClick={onBack}
                        className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        aria-label="Back to campaigns"
                    >
                        <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <h3 className="text-sm font-semibold text-gray-900">{group.campaignName}</h3>
                            <TrainingStatusPill status={derivedStatus as SessionStatus} />
                        </div>
                        <p className="text-xs text-gray-500">{group.personaName}</p>
                    </div>
                </div>
                <a
                    href={`/training/campaigns/${group.campaignId}`}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline flex-shrink-0 mt-0.5"
                >
                    View campaign
                    <ExternalLinkIcon className="w-3 h-3" />
                </a>
            </div>

            {startedAttempts.length === 0 ? (
                <EmptyPerformance />
            ) : (
                <div className="px-6 py-5 pb-12 space-y-5">
                    <PerformanceContent
                        attempts={sortedAttempts}
                        personaName={group.personaName}
                        sortKey={sortKey}
                        sortDir={sortDir}
                        historySorted={historySorted}
                        attemptCount={attemptCount}
                        scoredActive={scoredActive}
                        scores={scores}
                        total={total}
                        band={band}
                        onSort={handleSort}
                    />
                </div>
            )}
        </div>
    );
}

function AttemptHistoryTable({
    attempts,
    historySorted,
    sortKey,
    sortDir,
    onSort,
}: {
    attempts: TrainingSession[];
    historySorted: TrainingSession[];
    sortKey: SortKey;
    sortDir: SortDir;
    onSort: (key: SortKey) => void;
}) {
    const latestId = attempts[attempts.length - 1]?.id;
    return (
        <div className="border border-gray-100 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] text-gray-500 uppercase tracking-wide">
                    <tr>
                        <SortHeader label="ATTEMPT" active={sortKey === "attempt"} dir={sortDir} onClick={() => onSort("attempt")} />
                        <SortHeader label="DATE" active={sortKey === "date"} dir={sortDir} onClick={() => onSort("date")} />
                        <SortHeader label="STATUS" active={sortKey === "status"} dir={sortDir} onClick={() => onSort("status")} />
                        <SortHeader label="SCORE" active={sortKey === "score"} dir={sortDir} onClick={() => onSort("score")} />
                        <th className="px-3 py-2" />
                    </tr>
                </thead>
                <tbody>
                    {historySorted.map((a) => (
                        <tr key={a.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                                <span>#{a.attempt_number}</span>
                                {a.id === latestId && (
                                    <span className="ml-1.5 text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 rounded font-medium">
                                        Latest
                                    </span>
                                )}
                            </td>
                            <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                {formatDateGB(a.completed_at)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                <TrainingStatusPill status={a.status} />
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap font-semibold text-gray-900 tabular-nums">
                                {a.total_score !== null ? `${a.total_score}/100` : "—"}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                {a.status === "completed" && (
                                    <a
                                        href={`/training-results/${a.token}?mode=employer`}
                                        className="text-xs text-blue-600 hover:underline"
                                    >
                                        View Results
                                    </a>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function DrawerSkeleton() {
    return (
        <div className="px-6 py-5 space-y-5" aria-busy="true">
            <div className="rounded-lg border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="h-4 w-28 bg-gray-100 rounded animate-pulse" />
                    <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
                </div>
                <div className="h-16 w-full bg-gray-50 rounded animate-pulse" />
            </div>
            <div className="rounded-lg border border-gray-200 p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
                    <div className="h-5 w-20 bg-gray-100 rounded-full animate-pulse" />
                </div>
                <div className="h-8 w-20 bg-gray-100 rounded animate-pulse" />
                <div className="space-y-2">
                    <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                    <div className="h-3 w-4/5 bg-gray-100 rounded animate-pulse" />
                </div>
                <div className="space-y-2.5 pt-2">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex items-center gap-3">
                            <div className="h-3 w-28 bg-gray-100 rounded animate-pulse" />
                            <div className="h-2 flex-1 bg-gray-100 rounded animate-pulse" />
                            <div className="h-3 w-8 bg-gray-100 rounded animate-pulse flex-shrink-0" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function SortHeader({
    label,
    active,
    dir,
    onClick,
}: {
    label: string;
    active: boolean;
    dir: SortDir;
    onClick: () => void;
}) {
    return (
        <th className="text-left px-3 py-2 whitespace-nowrap">
            <button
                type="button"
                onClick={onClick}
                className={cn(
                    "inline-flex items-center gap-1 hover:text-gray-700 transition-colors",
                    active ? "text-gray-700" : "text-gray-500",
                )}
            >
                <span>{label}</span>
                {active ? (
                    dir === "asc" ? (
                        <ArrowUpIcon className="w-3 h-3" />
                    ) : (
                        <ArrowDownIcon className="w-3 h-3" />
                    )
                ) : (
                    <ArrowUpDownIcon className="w-3 h-3 opacity-40" />
                )}
            </button>
        </th>
    );
}
